import {useEffect, useState} from "react";
import {EmployeeItem} from "../types/employee";
import {useForm} from "antd/es/form/Form";
import {employeeService} from "../services/employee-service";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";


export const useEmployeeController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [employeeList, setEmployeeList] = useState<EmployeeItem[]>([])
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeItem | null>(null)
  const [form] = useForm()


  useEffect(() => {
    if (!isCreateModalOpen) {
      form.resetFields()
      setSelectedEmployee(null)
    }
  }, [isCreateModalOpen])

  useEffect(() => {
    if (!isDetailModalOpen) {
      setSelectedEmployee(null)
    }
  }, [isDetailModalOpen])

  useEffect(() => {
    fetchEmployeeList([])
  }, [lastUpdate])

  const toggleCreateModalOpen = () => {
    setIsCreateModalOpen(!isCreateModalOpen)
  }

  const toggleDetailModal = () => {
    setIsDetailModalOpen(!isDetailModalOpen)
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now())
  }
  const showDetailButtonHandler = (employee: EmployeeItem) => {
    setSelectedEmployee(employee)
    toggleDetailModal()
  }

  const editButtonHandler = (data: EmployeeItem) => {
    setSelectedEmployee(data)
    form.setFieldsValue(data)
    toggleCreateModalOpen()
  }

  const deleteEmployee = async (id: string) => {
    setIsLoading(true)
    try {
      await employeeService.delete(id)
      toggleLastUpdate()
      openSuccessNotification("Працівника успішно видалено!")
    } catch (error) {
      showErrorNotification({message: "Помилка видалення працівника!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }


  const confirmDeleteEmployee = (props: { id: string, name: string }) => {
    const {id, name} = props
    const {confirm} = Modal;

    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити ${name} ?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          deleteEmployee(id)
        },
      });

  }

  const fetchEmployeeList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await employeeService.list(queries)
      setEmployeeList(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при отриманні даних!"})
    } finally {
      setIsLoading(false)
    }
  }

  const createEmployee = async (data: EmployeeItem) => {
    setIsLoading(true)
    try {
      await employeeService.createEmployee(data)
      toggleCreateModalOpen()
      toggleLastUpdate()
      openSuccessNotification("Працівника успішно створено!")
    } catch (error) {
      showErrorNotification({message: "Помилка при створенні працівника!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }

  const updateEmployee = async (data: EmployeeItem) => {
    setIsLoading(true)
    try {
      await employeeService.updateEmployee(data)
      toggleCreateModalOpen()
      toggleLastUpdate()
      openSuccessNotification("Дані успішно оновлено!")
    } catch (error) {
      showErrorNotification({message: "Помилка при оновленні даних працівника!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }

  const onFormSubmit = async (employeeData: EmployeeItem) => {
    if (selectedEmployee) {
      const correctData = {
        $id: selectedEmployee.$id,
        ...employeeData,
      }
      await updateEmployee(correctData)
    } else {
      await createEmployee(employeeData)
    }
  }

  const getEmployeeData = async (id: string) => {
    setIsLoading(true)
    try {
      const data = await employeeService.getById(id)
      setSelectedEmployee(data)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при завантаженні даних!"})
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    toggleDetailModal,
    isDetailModalOpen,
    showDetailButtonHandler,
    selectedEmployee,
    toggleCreateModalOpen,
    isCreateModalOpen,
    form,
    employeeList,
    onFormSubmit,
    confirmDeleteEmployee,
    editButtonHandler,
    getEmployeeData
  }
}

export default useEmployeeController